// ═══════════════════════════════════════════════════════════════
// Impact — Assessment List (Table view with filters, sort, export)
// Ported from Firebase AssessmentList.jsx → OpenGov Ignatius backend
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, TextInput, Select, Button, Table,
  Group, Stack, Badge, Pagination, Center, ActionIcon,
  Tooltip, Menu, Loader,
} from '../../components/gab/GabComponents';
import { DatePickerInput } from '../../components/gab/MantineDates';
import {
  IconSearch, IconDownload, IconSortAscending, IconSortDescending,
  IconSelector, IconPlus, IconFilter, IconFilterOff, IconEye,
} from '@tabler/icons-react';
import { useImpactStore, type Assessment } from '../../store/impactStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { assessmentsToCSV } from './lib/femaForms';

// ── Constants ──

const PAGE_SIZE = 25;

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'individual', label: 'Individual (IA)' },
  { value: 'public', label: 'Public (PA)' },
];

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'affected', label: 'Affected' },
  { value: 'minor', label: 'Minor' },
  { value: 'major', label: 'Major' },
  { value: 'destroyed', label: 'Destroyed' },
  { value: 'inaccessible', label: 'Inaccessible' },
];

const SEVERITY_OPTIONS = [
  { value: '', label: 'All Severities' },
  { value: '1', label: 'Severity 1' },
  { value: '2', label: 'Severity 2' },
  { value: '3', label: 'Severity 3' },
  { value: '4', label: 'Severity 4' },
  { value: '5', label: 'Severity 5' },
];

const CATEGORY_COLORS: Record<string, string> = {
  affected: 'green',
  minor: 'yellow',
  major: 'orange',
  destroyed: 'red',
  inaccessible: 'violet',
};

type SortField = 'datecreated' | 'address' | 'damageCategory' | 'severity' | 'assessmentType' | 'assessorName';
type SortDir = 'asc' | 'desc';

// ── Component ──

export default function AssessmentList() {
  const navigate = useNavigate();
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('impact');
  const { assessments, loading: isLoading, loadAssessments } = useImpactStore();

  // Filters
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Sort
  const [sortField, setSortField] = useState<SortField>('datecreated');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Pagination
  const [page, setPage] = useState(1);

  // Load on mount
  useEffect(() => {
    loadAssessments();
  }, [loadAssessments]);

  // Filter to active incident
  const incidentAssessments = useMemo(() => {
    if (!activeIncident) return [];
    return assessments.filter(a => String(a.incidentId) === String(activeIncident.id));
  }, [assessments, activeIncident]);

  // Apply filters
  const filtered = useMemo(() => {
    let result = incidentAssessments;

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(a =>
        (a.address || '').toLowerCase().includes(q) ||
        (a.assessorName || '').toLowerCase().includes(q) ||
        (a.description || '').toLowerCase().includes(q) ||
        (a.facilityName || '').toLowerCase().includes(q) ||
        (a.county || '').toLowerCase().includes(q)
      );
    }

    if (typeFilter) {
      result = result.filter(a => a.assessmentType === typeFilter);
    }

    if (categoryFilter) {
      result = result.filter(a => a.damageCategory === categoryFilter);
    }

    if (severityFilter) {
      result = result.filter(a => String(a.severity) === severityFilter);
    }

    if (dateFrom) {
      result = result.filter(a => {
        const d = new Date(a.datecreated);
        return !isNaN(d.getTime()) && d >= dateFrom;
      });
    }

    if (dateTo) {
      const endOfDay = new Date(dateTo);
      endOfDay.setHours(23, 59, 59, 999);
      result = result.filter(a => {
        const d = new Date(a.datecreated);
        return !isNaN(d.getTime()) && d <= endOfDay;
      });
    }

    return result;
  }, [incidentAssessments, search, typeFilter, categoryFilter, severityFilter, dateFrom, dateTo]);

  // Sort
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'datecreated':
          cmp = (a.datecreated || '').localeCompare(b.datecreated || '');
          break;
        case 'address':
          cmp = (a.address || '').localeCompare(b.address || '');
          break;
        case 'damageCategory':
          cmp = (a.damageCategory || '').localeCompare(b.damageCategory || '');
          break;
        case 'severity':
          cmp = (a.severity || 0) - (b.severity || 0);
          break;
        case 'assessmentType':
          cmp = (a.assessmentType || '').localeCompare(b.assessmentType || '');
          break;
        case 'assessorName':
          cmp = (a.assessorName || '').localeCompare(b.assessorName || '');
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortField, sortDir]);

  // Paginate
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, categoryFilter, severityFilter, dateFrom, dateTo]);

  // ── Sort Toggle ──

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <IconSelector size={14} style={{ opacity: 0.3 }} />;
    return sortDir === 'asc' ? <IconSortAscending size={14} /> : <IconSortDescending size={14} />;
  };

  // ── CSV Export ──

  const handleExport = () => {
    const csv = assessmentsToCSV(filtered);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `assessments-${activeIncident?.name || 'export'}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Clear Filters ──

  const hasActiveFilters = !!(search || typeFilter || categoryFilter || severityFilter || dateFrom || dateTo);

  const clearFilters = () => {
    setSearch('');
    setTypeFilter('');
    setCategoryFilter('');
    setSeverityFilter('');
    setDateFrom(null);
    setDateTo(null);
  };

  // ── No Incident Guard ──

  if (!activeIncident) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <Text size="lg" fw={600} c="dimmed">No Active Incident</Text>
          <Text size="sm" c="dimmed">Select an incident to view assessments.</Text>
          <Button variant="light" onClick={() => navigate('/incidents')}>
            Go to Incidents
          </Button>
        </Stack>
      </Center>
    );
  }

  // ── Render ──

  return (
    <div>
      {/* Header */}
      <Group justify="space-between" mb="md">
        <div>
          <Title order={3} c="black">Assessments</Title>
          <Text size="xs" c="dimmed">
            {activeIncident.name} — {filtered.length} record{filtered.length !== 1 ? 's' : ''}
            {hasActiveFilters ? ' (filtered)' : ''}
          </Text>
        </div>
        <Group gap="sm">
          <Button
            variant="light"
            size="xs"
            leftSection={showFilters ? <IconFilterOff size={14} /> : <IconFilter size={14} />}
            onClick={() => setShowFilters(!showFilters)}
            color={hasActiveFilters ? 'blue' : 'gray'}
          >
            Filters
          </Button>
          <Menu shadow="md" width={180}>
            <Menu.Target>
              <Button variant="light" size="xs" leftSection={<IconDownload size={14} />}>
                Export
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={handleExport}>
                Export as CSV
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
          {canEdit && (
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => navigate('/impact/capture')}
            >
              New Assessment
            </Button>
          )}
        </Group>
      </Group>

      {/* Search Bar */}
      <TextInput
        placeholder="Search by address, assessor, description..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        mb="sm"
      />

      {/* Filters Panel */}
      {showFilters && (
        <Paper p="sm" radius="sm" withBorder mb="sm">
          <Group gap="sm" align="flex-end">
            <Select
              label="Type"
              data={TYPE_OPTIONS}
              value={typeFilter}
              onChange={(v) => setTypeFilter(v || '')}
              size="xs"
              w={140}
              clearable
            />
            <Select
              label="Category"
              data={CATEGORY_OPTIONS}
              value={categoryFilter}
              onChange={(v) => setCategoryFilter(v || '')}
              size="xs"
              w={140}
              clearable
            />
            <Select
              label="Severity"
              data={SEVERITY_OPTIONS}
              value={severityFilter}
              onChange={(v) => setSeverityFilter(v || '')}
              size="xs"
              w={130}
              clearable
            />
            <DatePickerInput
              label="From"
              value={dateFrom}
              onChange={(v) => setDateFrom(v as Date | null)}
              size="xs"
              w={140}
              clearable
            />
            <DatePickerInput
              label="To"
              value={dateTo}
              onChange={(v) => setDateTo(v as Date | null)}
              size="xs"
              w={140}
              clearable
            />
            {hasActiveFilters && (
              <Button variant="subtle" size="xs" color="red" onClick={clearFilters}>
                Clear All
              </Button>
            )}
          </Group>
        </Paper>
      )}

      {/* Loading State */}
      {isLoading && (
        <Center py="xl">
          <Loader size="md" />
        </Center>
      )}

      {/* Empty State */}
      {!isLoading && filtered.length === 0 && (
        <Center py={60}>
          <Stack align="center" gap="sm">
            <Text size="lg" fw={600} c="dimmed">
              {hasActiveFilters ? 'No Matching Assessments' : 'No Assessments Yet'}
            </Text>
            <Text size="sm" c="dimmed">
              {hasActiveFilters
                ? 'Try adjusting your filters.'
                : 'Start by creating a new assessment using Quick Capture.'}
            </Text>
            {!hasActiveFilters && canEdit && (
              <Button
                variant="light"
                leftSection={<IconPlus size={14} />}
                onClick={() => navigate('/impact/capture')}
              >
                Quick Capture
              </Button>
            )}
          </Stack>
        </Center>
      )}

      {/* Table */}
      {!isLoading && filtered.length > 0 && (
        <>
          <Paper withBorder radius="sm" style={{ overflow: 'auto' }}>
            <Table striped highlightOnHover verticalSpacing="xs" horizontalSpacing="sm" style={{ minWidth: 800 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('assessmentType')}>
                    <Group gap={4} wrap="nowrap">Type <SortIcon field="assessmentType" /></Group>
                  </Table.Th>
                  <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('address')}>
                    <Group gap={4} wrap="nowrap">Address <SortIcon field="address" /></Group>
                  </Table.Th>
                  <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('damageCategory')}>
                    <Group gap={4} wrap="nowrap">Category <SortIcon field="damageCategory" /></Group>
                  </Table.Th>
                  <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('severity')}>
                    <Group gap={4} wrap="nowrap">Severity <SortIcon field="severity" /></Group>
                  </Table.Th>
                  <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('assessorName')}>
                    <Group gap={4} wrap="nowrap">Assessor <SortIcon field="assessorName" /></Group>
                  </Table.Th>
                  <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('datecreated')}>
                    <Group gap={4} wrap="nowrap">Date <SortIcon field="datecreated" /></Group>
                  </Table.Th>
                  <Table.Th w={50}>
                    {/* Actions */}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {paginated.map((a) => (
                  <AssessmentRow key={a.id} assessment={a} onView={() => navigate(`/impact/assessment/${a.id}`)} />
                ))}
              </Table.Tbody>
            </Table>
          </Paper>

          {/* Pagination */}
          {totalPages > 1 && (
            <Group justify="space-between" mt="sm">
              <Text size="xs" c="dimmed">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} of {sorted.length}
              </Text>
              <Pagination
                total={totalPages}
                value={page}
                onChange={setPage}
                size="sm"
              />
            </Group>
          )}
        </>
      )}
    </div>
  );
}

// ── Row Component ──

function AssessmentRow({ assessment: a, onView }: { assessment: Assessment; onView: () => void }) {
  const typeLabel = a.assessmentType === 'individual' ? 'IA' : a.assessmentType === 'public' ? 'PA' : a.assessmentType;
  const typeColor = a.assessmentType === 'individual' ? 'blue' : 'teal';

  const categoryColor = CATEGORY_COLORS[a.damageCategory] || 'gray';

  const displayAddress = a.address
    ? a.address.length > 50 ? a.address.slice(0, 50) + '...' : a.address
    : '(no address)';

  const displayDate = a.datecreated
    ? (() => {
        const d = new Date(a.datecreated);
        return isNaN(d.getTime()) ? a.datecreated : d.toLocaleDateString();
      })()
    : '';

  return (
    <Table.Tr style={{ cursor: 'pointer' }} onClick={onView}>
      <Table.Td>
        <Badge size="sm" variant="light" color={typeColor}>{typeLabel}</Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm" lineClamp={1}>{displayAddress}</Text>
        {a.county && <Text size="xs" c="dimmed">{a.county}</Text>}
      </Table.Td>
      <Table.Td>
        {a.damageCategory ? (
          <Badge size="sm" variant="light" color={categoryColor}>
            {a.damageCategory}
          </Badge>
        ) : (
          <Text size="xs" c="dimmed">—</Text>
        )}
      </Table.Td>
      <Table.Td>
        {a.severity ? (
          <Badge
            size="sm"
            variant="filled"
            color={a.severity >= 4 ? 'red' : a.severity >= 3 ? 'orange' : a.severity >= 2 ? 'yellow' : 'green'}
          >
            {a.severity}
          </Badge>
        ) : (
          <Text size="xs" c="dimmed">—</Text>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="sm" lineClamp={1}>{a.assessorName || '—'}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="xs">{displayDate}</Text>
      </Table.Td>
      <Table.Td>
        <Tooltip label="View Assessment">
          <ActionIcon variant="subtle" size="sm" onClick={(e) => { e.stopPropagation(); onView(); }}>
            <IconEye size={16} />
          </ActionIcon>
        </Tooltip>
      </Table.Td>
    </Table.Tr>
  );
}
